import { Code, ConnectError } from '@connectrpc/connect';

/**
 * Converts an unknown thrown value (typically a ConnectError raised by a BFF gRPC
 * call) into an operator-friendly message. The gRPC status code drives the
 * phrasing so a transient infrastructure failure reads differently from a genuine
 * "not found", and the raw server message is appended only when it adds signal.
 *
 * `action` should read naturally after "Couldn't ", e.g. "delete this user".
 */
export const formatRpcError = (error: unknown, action: string): string => {
  const connectError = ConnectError.from(error);
  const detail = connectError.rawMessage.trim();

  switch (connectError.code) {
    case Code.Unavailable:
      return `Couldn't ${action}: a required service is temporarily unavailable. Please try again in a moment.`;
    case Code.DeadlineExceeded:
      return `Couldn't ${action}: the request timed out before it completed. It may still be processing — refresh before retrying.`;
    case Code.Unauthenticated:
    case Code.PermissionDenied:
      return `Couldn't ${action}: your session isn't authorized for this action. Try signing in again.`;
    case Code.NotFound:
      return `Couldn't ${action}: the user could not be found.`;
    case Code.InvalidArgument:
      return `Couldn't ${action}: ${detail || 'the request was rejected as invalid.'}`;
    case Code.Unknown:
    case Code.Internal:
      return `Couldn't ${action}: the server hit an unexpected error${
        detail ? ` (${detail})` : ''
      }. Please retry; if it keeps failing, share this with engineering.`;
    default:
      return `Couldn't ${action}${detail ? `: ${detail}` : '.'}`;
  }
};
